import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Storage } from 'aws-amplify';
import { useAuth } from 'app/context/auth-context';
import { OrgService, Organization } from 'app/services/OrgService';
import { useS3Object } from 'app/context/S3ObjectContext';



const DragDropArea: React.FC<{ onFilesDrop: (files: File[]) => void }> = ({ onFilesDrop }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    onFilesDrop(files);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      onClick={handleClick}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${isDragging ? '#007bff' : '#ccc'}`,
        borderRadius: '4px',
        padding: '20px',
        textAlign: 'center',
        cursor: 'pointer',
        backgroundColor: isDragging ? '#f8f9fa' : 'white',
      }}
    >
      <p>Drag and drop company documents here, or click to select files</p>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={(e) => onFilesDrop(Array.from(e.target.files || []))}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export interface OrganizationFormHandle {
  submitForm: () => Promise<any>;
  isDirty: boolean;
}

const OrganizationForm = React.forwardRef<OrganizationFormHandle, { existingOrg: Organization | null, onSave: (() => void), sponsorId?: string }>((props, ref) => {
  const [logoThumbnail, setLogoThumbnail] = useState<string | null>(null);
  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<Array<{ key: string, name: string, url: string }>>([]);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { user } = useAuth();
  const orgService = new OrgService();
  const { invalidateS3ObjectCache } = useS3Object();

  const formik = useFormik({
    initialValues: {
      companyName: props.existingOrg?.companyName || '',
      companyDetails: props.existingOrg?.companyDetails || '',
      jobBoardLink: props.existingOrg?.jobBoardLink || 'https://',
      city: props.existingOrg?.city || '',
      state: props.existingOrg?.state || '',
      companyWebsite: props.existingOrg?.companyWebsite || 'https://',
      companyLogo: null as File | null,
      companyDocuments: [] as File[],
      sponsorId: props.sponsorId,
    },
    validationSchema: Yup.object({
      companyName: Yup.string()
        .max(100, 'Company Name must be 100 characters or less')
        .required('Company Name is required'),
      companyDetails: Yup.string().max(5000, 'Company Details must be 5000 characters or less'),
      jobBoardLink: Yup.string().max(500, 'Link to job board must be 500 characters or less'),
      city: Yup.string().required('City is required').max(100, 'City must be 100 characters or less'),
      state: Yup.string().required('State is required').max(50, 'State must be 50 characters or less'),
      companyWebsite: Yup.string()
        .required('Company website is required')
        .url('Must be a valid URL')
        .max(500, 'Website URL must be 500 characters or less'),
      companyLogo: Yup.mixed().test(
        'required-logo',
        'Company logo is required',
        function (value) {
          return !!value || !!props.existingOrg?.companyLogo; // Pass validation if logo exists in the existing organization
        }
      ),
      companyDocuments: Yup.array().of(Yup.mixed()),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        if (!user?.id) {
          console.error('User is not authenticated.');
          return;
        }

        let logoPath;

        if (values.companyLogo) {
          try {
            const file = values.companyLogo;
            const fileExtension = file.name.split('.').pop();
            logoPath = await Storage.put(`${user.organizationId}/company_logo/logo.${fileExtension}`, file, {
              contentType: file.type,
            });

            if (logoPath?.key) {
              invalidateS3ObjectCache(logoPath.key);
            }
          } catch (err) {
            console.error('Error uploading logo:', err);
            throw err; // Ensure the error propagates to the outer catch block
          }
        }

        // Upload Company Documents to S3 (if any)
        const documentKeys = await Promise.all(
          values.companyDocuments.map(async (doc) => {
            const path = await Storage.put(`${user.organizationId}/company_docs/${doc.name}`, doc, {
              contentType: doc.type,
            });
            if (path?.key) {
              invalidateS3ObjectCache(path.key);
            }
            return path;
          })
        );

        // Sending other form data to the backend via Axios
        const orgData: Partial<Organization> = {
          id: `${user.organizationId}`,
          companyName: values.companyName,
          companyDetails: values.companyDetails,
          jobBoardLink: values.jobBoardLink,
          companyLogo: logoPath?.key || props.existingOrg?.companyLogo || '',
          companyDocuments: documentKeys.map(doc => doc.key),
          city: values.city,
          state: values.state,
          companyWebsite: values.companyWebsite,
          onBoardingProgress: props.existingOrg?.onBoardingProgress || { progress: 20, lastCompletedStep: 'FilledOrganizationDetails' },
          createdBy: props.existingOrg?.createdBy || user.id,
          sponsorId: values.sponsorId || props.sponsorId
        };

        let savedOrg: Organization | undefined;

        if (props.existingOrg?.id) {
          savedOrg = await orgService.updateOrg(user.organizationId, orgData);
        } else {
          savedOrg = await orgService.createOrg(orgData);
        }

        // Verify organization was created/updated successfully using the response
        if (savedOrg && savedOrg.id) {
          props.onSave();
        } else {
          const errorMsg = 'Organization verification failed: Valid Org data not returned from backend';
          setSubmitError(errorMsg);
        }

      } catch (error) {
        console.error('Error in form submission:', error); // Debugging log
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  React.useImperativeHandle(ref, () => ({
    submitForm: () => {
      return formik.submitForm();
    },
    isDirty: formik.dirty
  }), [formik.dirty, formik.submitForm]);


  useEffect(() => {
    if (props.existingOrg) {
      try {
        // Set initial form values
        formik.setValues({
          companyName: props.existingOrg.companyName || '',
          companyDetails: props.existingOrg.companyDetails || '',
          city: props.existingOrg.city || '',
          state: props.existingOrg.state || '',
          companyWebsite: props.existingOrg.companyWebsite || 'https://',
          jobBoardLink: props.existingOrg.jobBoardLink || 'https://',
          companyLogo: null,
          companyDocuments: [],
          sponsorId: props.sponsorId,
        });
        // Set logo thumbnail if exists
        if (props.existingOrg.companyLogo) {
          try {
            (async () => {
              const logoUrl = await Storage.get(props.existingOrg!.companyLogo, {
                expires: 3600,
                cacheControl: 'no-cache',
                contentDisposition: 'inline',
              });
              setLogoThumbnail(logoUrl);
            })();
          } catch (error) {
            console.error('Error fetching logo:', error);
          }
        }

        (async () => {
          const existingDocs = await Promise.all(
            (props.existingOrg!.companyDocuments || []).map(async (docKey) => {
              const url = await Storage.get(docKey, { expires: 3600 });
              return { key: docKey, name: docKey.split('/').pop() || '', url };
            }));
          setExistingDocuments(existingDocs);

        })();
      } catch (error) {
        console.error('Error fetching organization data:', error);
      }
    }
  }, [props.existingOrg]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const files = event.currentTarget.files;
    if (files && files.length > 0) {
      if (fieldName === 'companyLogo') {
        const file = files[0];
        formik.setFieldValue(fieldName, file);
        setIsLogoUploading(true);
        // Create thumbnail
        const reader = new FileReader();
        reader.onload = (e) => {
          setLogoThumbnail(e.target?.result as string);
          setIsLogoUploading(false);
        };
        if (file instanceof Blob) {
          reader.readAsDataURL(file);
        }
      } else if (fieldName === 'companyDocuments') {
        const newDocuments = Array.from(files);
        formik.setFieldValue(fieldName, [...formik.values.companyDocuments, ...newDocuments]);
        setUploadedDocuments(prevDocs => [...prevDocs, ...newDocuments]);
      }
    }
  };

  const handleDocumentDelete = (index: number) => {
    const updatedDocuments = uploadedDocuments.filter((_, i) => i !== index);
    setUploadedDocuments(updatedDocuments);
    formik.setFieldValue('companyDocuments', updatedDocuments);
  };

  // Update your handleDocumentDelete function:
  const handleExistingDocumentDelete = async (index: number) => {
    const docToDelete = existingDocuments[index];
    try {
      await Storage.remove(docToDelete.key);
      const updatedDocs = existingDocuments.filter((_, i) => i !== index);
      setExistingDocuments(updatedDocs);
    } catch (error) {
      console.error('Error deleting document from S3:', error);
    }
  };

  const handleFilesDrop = (files: File[]) => {
    const newDocuments = [...formik.values.companyDocuments, ...files];
    formik.setFieldValue('companyDocuments', newDocuments);
    setUploadedDocuments(newDocuments);
  };

  return (
    <div className="card">
      <div className="card-header py-3">
        <h3 className="card-title text-dark">Organization Details</h3>
      </div>
      <div className="card-body">
        <form onSubmit={formik.handleSubmit}>
          {submitError && (
            <div className="alert alert-danger d-flex align-items-center mb-5" role="alert">
              <i className="fas fa-exclamation-circle me-3 fs-3 text-danger"></i>
              <div>{submitError}</div>
            </div>
          )}
          <div className="d-flex flex-column flex-lg-row">
            <div className="flex-lg-row-fluid me-lg-15 mb-10 mb-lg-0">
              <div className="mb-10">
                <label htmlFor="companyName" className="form-label fw-bold">Company Name</label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  maxLength={100}
                  className={`form-control ${formik.touched.companyName && formik.errors.companyName ? 'is-invalid' : ''}`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.companyName}
                />
                {formik.touched.companyName && formik.errors.companyName ? (
                  <div className="invalid-feedback">{formik.errors.companyName}</div>
                ) : null}
              </div>

              <div className="row mb-10">
                <div className="col-lg-6">
                  <label htmlFor="city" className="form-label fw-bold">City</label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    maxLength={100}
                    className={`form-control ${formik.touched.city && formik.errors.city ? 'is-invalid' : ''}`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.city}
                  />
                  {formik.touched.city && formik.errors.city ? (
                    <div className="invalid-feedback">{formik.errors.city}</div>
                  ) : null}
                </div>
                <div className="col-lg-6">
                  <label htmlFor="state" className="form-label fw-bold">State</label>
                  <input
                    id="state"
                    name="state"
                    type="text"
                    maxLength={50}
                    className={`form-control ${formik.touched.state && formik.errors.state ? 'is-invalid' : ''}`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.state}
                  />
                  {formik.touched.state && formik.errors.state ? (
                    <div className="invalid-feedback">{formik.errors.state}</div>
                  ) : null}
                </div>
              </div>

              <div className="mb-10">
                <label htmlFor="companyWebsite" className="form-label fw-bold">Company Website</label>
                <input
                  id="companyWebsite"
                  name="companyWebsite"
                  type="url"
                  maxLength={500}
                  className={`form-control ${formik.touched.companyWebsite && formik.errors.companyWebsite ? 'is-invalid' : ''}`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.companyWebsite}
                />
                {formik.touched.companyWebsite && formik.errors.companyWebsite ? (
                  <div className="invalid-feedback">{formik.errors.companyWebsite}</div>
                ) : null}
              </div>
              <div className="row mb-10">
                <div className="col-lg-12">
                  <label htmlFor="companyDetails" className="form-label fw-bold">Company Details</label>
                  <textarea
                    id="companyDetails"
                    name="companyDetails"
                    maxLength={5000}
                    className={`form-control ${formik.touched.companyDetails && formik.errors.companyDetails ? 'is-invalid' : ''}`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.companyDetails}
                    rows={5}
                  />
                  {formik.touched.companyDetails && formik.errors.companyDetails ? (
                    <div className="invalid-feedback">{formik.errors.companyDetails}</div>
                  ) : null}
                </div>
              </div>

            </div>
            <div className="separator-container d-none d-lg-flex">
              <div className="separator separator-solid bg-secondary h-100">&nbsp;</div>
            </div>
            <div className="flex-lg-row-fluid ms-lg-15">


              <div className="row mb-10">
                <div className="col-lg-12">
                  <label htmlFor="jobBoardLink" className="form-label fw-bold">Link to company job board</label>
                  <input
                    id="jobBoardLink"
                    name="jobBoardLink"
                    type="text"
                    maxLength={500}
                    className={`form-control ${formik.touched.jobBoardLink && formik.errors.jobBoardLink ? 'is-invalid' : ''}`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.jobBoardLink}
                  />
                  {formik.touched.jobBoardLink && formik.errors.jobBoardLink ? (
                    <div className="invalid-feedback">{formik.errors.jobBoardLink}</div>
                  ) : null}
                </div>
              </div>
              <div className="row mb-10">
                <div className="col-lg-12">
                  <label htmlFor="companyLogo" className="form-label fw-bold">Company Logo</label>
                  <div className="d-flex align-items-center">
                    <label htmlFor="companyLogo" className="btn btn-primary me-3">
                      <i className="fas fa-upload me-2"></i>Select Logo
                      <input
                        id="companyLogo"
                        name="companyLogo"
                        type="file"
                        accept="image/*,.jfif,.jpg,.jpeg"
                        className="d-none"
                        onChange={(e) => handleFileChange(e, 'companyLogo')}
                      />
                    </label>
                    {isLogoUploading ? (
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : logoThumbnail && (
                      <div className="ms-3">
                        <img
                          src={logoThumbnail}
                          alt="Logo preview"
                          crossOrigin="anonymous"
                          style={{ maxWidth: '100px', maxHeight: '100px' }}
                          onError={(e) => {
                            console.error('Error loading image:', e);
                            (e.target as HTMLImageElement).src = '/path/to/default-image.jpg'; // Fallback image
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {formik.touched.companyLogo && formik.errors.companyLogo ? (
                    <div className="invalid-feedback d-block">{formik.errors.companyLogo}</div>
                  ) : null}
                </div>
              </div>
              <div className="row mb-10">
                <div className="col-lg-12">
                  <label className="form-label fw-bold">Upload Company Documents</label>
                  <DragDropArea onFilesDrop={handleFilesDrop} />
                  {uploadedDocuments.length > 0 && (
                    <ul className="list-group mt-2">
                      {uploadedDocuments.map((doc, index) => (
                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          {doc.name}
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDocumentDelete(index)}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {existingDocuments.length > 0 && (
                    <ul className="list-group mt-2">
                      {existingDocuments.map((doc, index) => (
                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          {doc.name}
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => handleExistingDocumentDelete(index)}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 d-flex justify-content-end">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Next'}
              </button>
            </div>
          </div>
          {isSubmitting && (
            <div className="blockui-overlay" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0,0,0,0.05)',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div className="blockui-message">
                <span className="spinner-border text-primary"></span>
                <span className="ms-3">Processing...</span>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
});

export default OrganizationForm;
